import React, { createContext, useContext, useState } from 'react';
import { useAuth } from './AuthContext';

type AccountMode = 'live' | 'paper';

interface AccountModeContextType {
  mode: AccountMode;
  setMode: (mode: AccountMode) => void;
  isLive: boolean;
}

const AccountModeContext = createContext<AccountModeContextType | undefined>(undefined);

const STORAGE_KEY = 'accountMode';

export const AccountModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin } = useAuth();

  const [mode, setModeState] = useState<AccountMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'live' || stored === 'paper' ? stored : 'paper';
  });

  const setMode = (newMode: AccountMode) => {
    // Viewers cannot switch to live mode
    if (newMode === 'live' && !isAdmin) return;
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  };

  // Force viewers to paper even if they had live in localStorage
  const effectiveMode = !isAdmin && mode === 'live' ? 'paper' : mode;

  return (
    <AccountModeContext.Provider value={{ mode: effectiveMode, setMode, isLive: effectiveMode === 'live' }}>
      {children}
    </AccountModeContext.Provider>
  );
};

export const useAccountMode = (): AccountModeContextType => {
  const context = useContext(AccountModeContext);
  if (!context) {
    throw new Error('useAccountMode must be used within an AccountModeProvider');
  }
  return context;
};
